import React, { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { Spinner, Badge, Container } from "react-bootstrap";
import { Row, Col, Alert, Button, Form, Card, Modal } from "react-bootstrap";
import { API_ENDPOINTS } from "../../config/api";
import { useNavigate } from "react-router-dom";

const Profile = () => {
  const [user, setUser] = useState({});
  const [isLoading, setIsloading] = useState(false);
  const [error, setError] = useState(null);
  const { getAuthHeaders, token, user: authUser, login } = useAuth();
  const navigate = useNavigate();

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const [recordSaveMessage, setRecordSaveMessage] = useState(null);
  const [editForm, setEditForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    company: "",
    licenseNumber: "",
  });

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState(null);
  const [passwordError, setPasswordError] = useState(null);

  const formatApiError = (data) => {
    if (data?.errors?.length) {
      return data.errors.map((e) => e.msg || e.message).join(" ");
    }
    return data?.message || "Something went wrong. Please try again.";
  };

  const fetchProfileFromServer = async () => {
    const response = await fetch(API_ENDPOINTS.auth.getProfile, {
      headers: {
        "Content-Type": "application/json",
        "ngrok-skip-browser-warning": "true",
        ...getAuthHeaders(),
      },
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.data?.user) {
      return { ok: false, user: null, message: formatApiError(data) };
    }
    return { ok: true, user: data.data.user };
  };

  useEffect(() => {
    const fetchUser = async () => {
      setIsloading(true);
      setError(null);
      try {
        const result = await fetchProfileFromServer();
        if (!result.ok) {
          setError(result.message || "Could not load your profile.");
          setUser({});
          return;
        }
        setUser(result.user);
      } catch (err) {
        setError(err.message || "Could not load your profile.");
      } finally {
        setIsloading(false);
      }
    };
    fetchUser();
  }, [getAuthHeaders]);

  const handleBackNavigation = () => {
    if (window.history.length > 1) {
      navigate(-1);
      return;
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordMessage(null);
    setPasswordError(null);

    if (newPassword.length < 6) {
      setPasswordError("New password must be at least 6 characters.");
      return;
    }
    if (newPassword.length > 128) {
      setPasswordError("New password must be at most 128 characters.");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setPasswordError("New password and confirmation do not match.");
      return;
    }
    if (newPassword === currentPassword) {
      setPasswordError("New password must be different from your current password.");
      return;
    }

    setPasswordSaving(true);
    try {
      const response = await fetch(API_ENDPOINTS.auth.updatePassword, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setPasswordError(formatApiError(data));
        return;
      }

      setPasswordMessage("Password updated successfully.");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
    } catch (err) {
      setPasswordError(err.message || "Network error. Please try again.");
    } finally {
      setPasswordSaving(false);
    }
  };

  const openEditModal = () => {
    setEditError(null);
    setRecordSaveMessage(null);
    setEditForm({
      firstName: user.firstName || "",
      lastName: user.lastName || "",
      email: user.email || "",
      phone: user.phone || "",
      company: user.company || "",
      licenseNumber: user.licenseNumber || "",
    });
    setEditModalOpen(true);
  };

  const handleEditField = (field) => (e) => {
    setEditForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleUpdateRecord = async (e) => {
    e.preventDefault();
    setEditError(null);

    const phoneDigits = editForm.phone.replace(/\s/g, "");
    if (!/^[+]?[1-9]\d{0,15}$/.test(phoneDigits)) {
      setEditError("Enter a valid phone number (optional + prefix, then digits).");
      return;
    }

    setEditSaving(true);
    try {
      const payload = {
        firstName: editForm.firstName.trim(),
        lastName: editForm.lastName.trim(),
        email: editForm.email.trim().toLowerCase(),
        phone: phoneDigits,
        company: editForm.company.trim(),
        licenseNumber: editForm.licenseNumber.trim(),
      };

      const response = await fetch(API_ENDPOINTS.auth.updateDetails, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "ngrok-skip-browser-warning": "true",
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setEditError(formatApiError(data));
        return;
      }
      if (data.success === false) {
        setEditError(data.message || "Update failed.");
        return;
      }

      const refreshed = await fetchProfileFromServer();
      if (refreshed.ok && refreshed.user) {
        setUser(refreshed.user);
        if (token && authUser) {
          const u = refreshed.user;
          login(token, {
            ...authUser,
            firstName: u.firstName,
            lastName: u.lastName,
            email: u.email,
            company: u.company,
            licenseNumber: u.licenseNumber,
          });
        }
      } else {
        const updated = data.data?.user;
        if (updated) {
          setUser((prev) => ({ ...prev, ...updated }));
          if (token && authUser) {
            login(token, {
              ...authUser,
              firstName: updated.firstName,
              lastName: updated.lastName,
              email: updated.email,
              company: updated.company,
              licenseNumber: updated.licenseNumber,
            });
          }
        } else {
          setEditError(
            "Your changes may have been saved, but we could not reload your profile. Refresh the page."
          );
          setEditModalOpen(false);
          return;
        }
      }

      setEditModalOpen(false);
      setRecordSaveMessage(
        data.message || "Your profile was saved successfully. Changes are shown below."
      );
    } catch (err) {
      setEditError(err.message || "Network error. Please try again.");
    } finally {
      setEditSaving(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: "100vh" }}
      >
        <Spinner animation="border" role="status" variant="primary" size="lg">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Container fluid className="py-5">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Profile</Alert.Heading>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  const InfoItem = ({ label, value }) => (
    <div className="mb-4 pb-3 border-bottom">
      <div
        className="text-muted mb-1"
        style={{ fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.5px" }}
      >
        {label}
      </div>
      <div className="fw-semibold" style={{ fontSize: "1.1rem" }}>
        {value || "N/A"}
      </div>
    </div>
  );

  return (
    <div
      className="min-vh-100"
      //   style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" }}
    >
      <Container fluid className="py-5 px-4">
        <Row className="mb-5">
          <Col>
            <div className="d-flex align-items-center justify-content-between flex-wrap">
              <div className="d-flex align-items-center mb-3 mb-md-0">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center text-black fw-bold me-3"
                  style={{
                    width: "80px",
                    height: "80px",
                    fontSize: "2rem",
                    background: "rgba(255, 255, 255, 0.2)",
                    border: "3px solid rgba(0, 0, 0, 0.3)",
                  }}
                >
                  {user.firstName?.[0]}
                  {user.lastName?.[0]}
                </div>
                <div className="text-black">
                  <h1 className="mb-1 fw-bold">
                    {user.firstName} {user.lastName}
                  </h1>
                  <p className="mb-0 opacity-75" style={{ fontSize: "1.1rem" }}>
                    {user.email}
                  </p>
                </div>
              </div>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <Button variant="outline-secondary" size="sm" onClick={handleBackNavigation}>
                  Back
                </Button>
                <Button
                  type="button"
                  variant="outline-primary"
                  size="sm"
                  className="w-auto"
                  onClick={openEditModal}
                >
                  Update record
                </Button>
                <Badge bg="success" className="px-4 py-2" style={{ fontSize: "1rem" }}>
                  Active
                </Badge>
              </div>
            </div>
          </Col>
        </Row>

        {recordSaveMessage && (
          <Alert
            variant="success"
            className="mb-4"
            dismissible
            onClose={() => setRecordSaveMessage(null)}
          >
            <strong>Saved.</strong> {recordSaveMessage}
          </Alert>
        )}

        <div className="bg-white rounded-3 shadow p-4 p-md-5">
          <h3 className="mb-4 pb-2 border-bottom border-2 border-primary">Personal Information</h3>
          <Row>
            <Col md={6} lg={4} className="mb-3">
              <InfoItem label="First Name" value={user.firstName} />
            </Col>
            <Col md={6} lg={4} className="mb-3">
              <InfoItem label="Last Name" value={user.lastName} />
            </Col>
            <Col md={6} lg={4} className="mb-3">
              <InfoItem label="Email" value={user.email} />
            </Col>
            <Col md={6} lg={4} className="mb-3">
              <InfoItem label="Phone" value={user.phone} />
            </Col>
            <Col md={6} lg={4} className="mb-3">
              <InfoItem label="Company" value={user.company} />
            </Col>
            <Col md={6} lg={4} className="mb-3">
              <InfoItem label="License Number" value={user.licenseNumber} />
            </Col>
            <Col md={6} lg={4} className="mb-3">
              <InfoItem label="Joined" value={formatDate(user.joinedAt)} />
            </Col>
            <Col md={6} lg={4} className="mb-3">
              <InfoItem label="Last Login" value={formatDate(user.lastLogin)} />
            </Col>
          </Row>
        </div>

        <Card className="mt-4 shadow-sm border-0">
          <Card.Body className="p-4 p-md-5">
            <h3 className="mb-4 pb-2 border-bottom border-2 border-primary">Change password</h3>
            <p className="text-muted mb-4">
              Enter your current password, then choose a new one. Your session stays active after a
              successful change.
            </p>
            {passwordMessage && (
              <Alert variant="success" dismissible onClose={() => setPasswordMessage(null)}>
                {passwordMessage}
              </Alert>
            )}
            {passwordError && (
              <Alert variant="danger" dismissible onClose={() => setPasswordError(null)}>
                {passwordError}
              </Alert>
            )}
            <Form onSubmit={handleChangePassword} autoComplete="on">
              <Form.Group className="mb-3" controlId="profile-current-password">
                <Form.Label>Current password</Form.Label>
                <Form.Control
                  type="password"
                  name="current-password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  disabled={passwordSaving}
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="profile-new-password">
                <Form.Label>New password</Form.Label>
                <Form.Control
                  type="password"
                  name="new-password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  maxLength={128}
                  disabled={passwordSaving}
                />
                <Form.Text className="text-muted">At least 6 characters, up to 128.</Form.Text>
              </Form.Group>
              <Form.Group className="mb-4" controlId="profile-confirm-password">
                <Form.Label>Confirm new password</Form.Label>
                <Form.Control
                  type="password"
                  name="confirm-new-password"
                  autoComplete="new-password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  required
                  minLength={6}
                  maxLength={128}
                  disabled={passwordSaving}
                />
              </Form.Group>
              <Button type="submit" variant="primary" disabled={passwordSaving}>
                {passwordSaving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Updating…
                  </>
                ) : (
                  "Update password"
                )}
              </Button>
            </Form>
          </Card.Body>
        </Card>

        <Modal
          show={editModalOpen}
          onHide={() => !editSaving && setEditModalOpen(false)}
          centered
          size="lg"
        >
          <Modal.Header closeButton={!editSaving}>
            <Modal.Title>Update record</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleUpdateRecord}>
            <Modal.Body>
              {editError && (
                <Alert
                  variant="danger"
                  className="mb-3 py-2"
                  onClose={() => setEditError(null)}
                  dismissible
                >
                  {editError}
                </Alert>
              )}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="edit-first-name">
                    <Form.Label>First name</Form.Label>
                    <Form.Control
                      type="text"
                      autoComplete="given-name"
                      value={editForm.firstName}
                      onChange={handleEditField("firstName")}
                      required
                      disabled={editSaving}
                      maxLength={50}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3" controlId="edit-last-name">
                    <Form.Label>Last name</Form.Label>
                    <Form.Control
                      type="text"
                      autoComplete="family-name"
                      value={editForm.lastName}
                      onChange={handleEditField("lastName")}
                      required
                      disabled={editSaving}
                      maxLength={50}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3" controlId="edit-email">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  autoComplete="email"
                  value={editForm.email}
                  onChange={handleEditField("email")}
                  required
                  disabled={editSaving}
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="edit-phone">
                <Form.Label>Phone</Form.Label>
                <Form.Control
                  type="tel"
                  autoComplete="tel"
                  value={editForm.phone}
                  onChange={handleEditField("phone")}
                  required
                  disabled={editSaving}
                  placeholder="+14165550100"
                />
                <Form.Text className="text-muted">
                 
                </Form.Text>
              </Form.Group>
              <Form.Group className="mb-3" controlId="edit-company">
                <Form.Label>Company</Form.Label>
                <Form.Control
                  type="text"
                  autoComplete="organization"
                  value={editForm.company}
                  onChange={handleEditField("company")}
                  required
                  disabled={editSaving}
                />
              </Form.Group>
              <Form.Group className="mb-0" controlId="edit-license">
                <Form.Label>License number</Form.Label>
                <Form.Control
                  type="text"
                  value={editForm.licenseNumber}
                  onChange={handleEditField("licenseNumber")}
                  required
                  disabled={editSaving}
                />
              </Form.Group>
            </Modal.Body>
            <Modal.Footer className="border-0 pt-0">
              <Button
                type="button"
                variant="outline-secondary"
                className="w-auto"
                disabled={editSaving}
                onClick={() => setEditModalOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" variant="primary" className="w-auto" disabled={editSaving}>
                {editSaving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Saving…
                  </>
                ) : (
                  "Save changes"
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </div>
  );
};

export default Profile;
